import { IndexedTsPoint } from '@toggle/design-system';
import { z } from 'zod';

import { Indicator } from '~/api/timeseries/timeseries-schema';
import { indicatorsThresholds } from '~/views/indicators-widget/indicator-constants';

export type Point = {
  value: number;
  index: string;
};
export type Records = Record<Indicator | string, IndexedTsPoint[]> | null;
export type ThresholdsDates = Record<string, string> | undefined;
export type Data = Record<Indicator, Point[]> | null;

export const initialState = {
  indicators: { data: null, tsData: null, thresholdsDates: undefined },
  error: false,
  loading: true,
};

export const indicatorsType: Indicator[] = [
  Indicator.TLI,
  Indicator.RFI,
  Indicator.PI,
  Indicator.TI,
];

export const format = (
  points: {
    value: number;
    index: string;
  }[],
  featureId: Indicator,
  activeId: Indicator | undefined
) => {
  const thresholds: Record<string, string> = {};

  const tsData = points.map((p, i) => {
    if (featureId === activeId) {
      const threshold = indicatorsThresholds[activeId];

      if (p.value >= threshold.up || p.value <= threshold.down) {
        thresholds[p.index] =
          p.value >= threshold.up
            ? threshold.variants[0]
            : threshold.variants[1];
      }
    }

    return {
      index: i,
      value: p.value,
      date: new Date(p.index),
    };
  });

  return { tsData, thresholds };
};

export const getNewIndicators = (
  activeId: Indicator,
  data: Data,
  tsData: Records
) => {
  if (!data || !tsData) {
    return initialState.indicators;
  }

  const { tsData: newTsData, thresholds: thresholdsDates } = format(
    data[activeId],
    activeId,
    activeId
  );
  tsData[activeId] = newTsData;

  return { data, tsData, thresholdsDates };
};

export const formatIndicators = (
  timeSeriesData: Data,
  activeId: Indicator | undefined
) => {
  if (!timeSeriesData) {
    return initialState.indicators;
  }

  let indicatorFormatted: Records = {};
  let thresholdsDates: Record<string, string> = {};

  for (let id of indicatorsType) {
    const featureId = Indicator[id];

    const { tsData, thresholds } = format(
      timeSeriesData[featureId],
      featureId,
      activeId
    );

    thresholdsDates = {
      ...thresholdsDates,
      ...thresholds,
    };

    indicatorFormatted[featureId] = tsData;
  }

  return { data: timeSeriesData, tsData: indicatorFormatted, thresholdsDates };
};

export const indexedTSPointSchema = z.object({
  index: z.number(),
  value: z.number(),
  date: z.date(),
});
